import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { Calendar, User, Trash2, Plus, CheckCircle, Facebook, Twitter, Linkedin, Instagram, Link as LinkIcon } from 'lucide-react';
import T from '../T';

interface Article {
  id: string;
  title: string;
  content: string;
  authorName: string;
  authorId: string;
  imageUrl?: string;
  imageUrls?: string[];
  status?: 'pending' | 'approved';
  createdAt: any;
}

const MAX_ARTICLE_IMAGES = 3;

const ArticlesPage: React.FC = () => {
  const { user, role } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedShareKey, setCopiedShareKey] = useState<string | null>(null);

  useEffect(() => {
    const approvedQuery = query(
      collection(db, 'articles'),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    if (role === 'admin') {
      const adminQuery = query(collection(db, 'articles'), orderBy('createdAt', 'desc'));
      const unsubscribe = onSnapshot(adminQuery, (snapshot) => {
        const articlesData = snapshot.docs.map((articleDoc) => ({
          id: articleDoc.id,
          ...articleDoc.data()
        })) as Article[];
        setArticles(articlesData);
        setLoading(false);
      }, () => {
        setArticles([]);
        setLoading(false);
      });

      return () => unsubscribe();
    }

    if (role === 'lawyer' && user) {
      let approvedArticles: Article[] = [];
      let ownArticles: Article[] = [];

      const mergeArticles = () => {
        const merged = [...approvedArticles, ...ownArticles];
        const uniqueArticles = Array.from(
          new Map(merged.map((article) => [article.id, article])).values()
        ).sort((left, right) => {
          const leftMillis = left.createdAt?.toMillis ? left.createdAt.toMillis() : 0;
          const rightMillis = right.createdAt?.toMillis ? right.createdAt.toMillis() : 0;
          return rightMillis - leftMillis;
        });

        setArticles(uniqueArticles);
        setLoading(false);
      };

      const ownQuery = query(
        collection(db, 'articles'),
        where('authorId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubscribeApproved = onSnapshot(approvedQuery, (snapshot) => {
        approvedArticles = snapshot.docs.map((articleDoc) => ({
          id: articleDoc.id,
          ...articleDoc.data()
        })) as Article[];
        mergeArticles();
      }, () => {
        approvedArticles = [];
        mergeArticles();
      });

      const unsubscribeOwn = onSnapshot(ownQuery, (snapshot) => {
        ownArticles = snapshot.docs.map((articleDoc) => ({
          id: articleDoc.id,
          ...articleDoc.data()
        })) as Article[];
        mergeArticles();
      }, () => {
        ownArticles = [];
        mergeArticles();
      });

      return () => {
        unsubscribeApproved();
        unsubscribeOwn();
      };
    }

    const unsubscribe = onSnapshot(approvedQuery, (snapshot) => {
      const articlesData = snapshot.docs.map((articleDoc) => ({
        id: articleDoc.id,
        ...articleDoc.data()
      })) as Article[];
      setArticles(articlesData);
      setLoading(false);
    }, () => {
      setArticles([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [role, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !title || !content) return;

    if (selectedImages.length > MAX_ARTICLE_IMAGES) {
      setImageError(`You can upload up to ${MAX_ARTICLE_IMAGES} images per article.`);
      return;
    }

    try {
      setPublishing(true);
      setImageError('');

      const imageUrls: string[] = [];

      for (const imageFile of selectedImages) {
        const safeFileName = imageFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const imageRef = ref(
          storage,
          `vault/${user.uid}/article-images/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeFileName}`,
        );
        await uploadBytes(imageRef, imageFile, { contentType: imageFile.type || 'image/jpeg' });
        const downloadUrl = await getDownloadURL(imageRef);
        imageUrls.push(downloadUrl);
      }

      await addDoc(collection(db, 'articles'), {
        title,
        content,
        authorId: user.uid,
        authorName: user.displayName || 'Lawyer',
        imageUrls,
        imageUrl: imageUrls[0] || '',
        status: role === 'admin' ? 'approved' : 'pending',
        createdAt: serverTimestamp()
      });
      setTitle('');
      setContent('');
      setSelectedImages([]);
      setShowForm(false);
    } catch (error) {
      console.error("Error adding article:", error);
      setImageError('Unable to upload one or more images. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(event.target.files || []);

    if (fileList.length === 0) {
      setSelectedImages([]);
      setImageError('');
      return;
    }

    if (fileList.length > MAX_ARTICLE_IMAGES) {
      setSelectedImages(fileList.slice(0, MAX_ARTICLE_IMAGES));
      setImageError(`Only the first ${MAX_ARTICLE_IMAGES} images were selected.`);
      return;
    }

    setSelectedImages(fileList);
    setImageError('');
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await deleteDoc(doc(db, 'articles', id));
      } catch (error) {
        console.error("Error deleting article:", error);
      }
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await updateDoc(doc(db, 'articles', id), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: user?.uid || null,
      });
    } catch (error) {
      console.error("Error approving article:", error);
    }
  };

  const visibleArticles = articles;

  const getArticleUrl = (articleKey: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://yliu.ca';
    return `${origin}/articles#${articleKey}`;
  };

  const copyShareUrl = async (articleKey: string) => {
    const url = getArticleUrl(articleKey);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedShareKey(articleKey);
      setTimeout(() => setCopiedShareKey((current) => (current === articleKey ? null : current)), 2000);
    } catch (error) {
      console.error('Unable to copy article URL:', error);
    }
  };

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderShareActions = (articleKey: string, articleTitle: string) => {
    const articleUrl = getArticleUrl(articleKey);
    const encodedUrl = encodeURIComponent(articleUrl);
    const encodedTitle = encodeURIComponent(articleTitle);

    return (
      <div style={{ marginTop: '1.25rem' }}>
        <p style={{ marginBottom: '0.6rem', fontSize: '0.9rem', color: '#555' }}><T>Share this article</T></p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '8px 12px' }}
            onClick={() => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}
          >
            <Facebook size={16} /> Facebook
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '8px 12px' }}
            onClick={() => openShareWindow(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`)}
          >
            <Twitter size={16} /> Twitter
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '8px 12px' }}
            onClick={() => openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`)}
          >
            <Linkedin size={16} /> LinkedIn
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '8px 12px' }}
            onClick={() => {
              void copyShareUrl(articleKey);
              openShareWindow('https://www.instagram.com/');
            }}
          >
            <Instagram size={16} /> Instagram
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '8px 12px' }}
            onClick={() => void copyShareUrl(articleKey)}
          >
            <LinkIcon size={16} /> {copiedShareKey === articleKey ? 'Copied URL' : 'Copy URL'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="container" style={{ padding: '40px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <h1 style={{ color: '#0f172a' }}><T>Recent Articles & Legal Insights</T></h1>
        {(role === 'lawyer' || role === 'admin') && (
          <button 
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={20} />
            <T>{showForm ? 'Cancel' : 'Post Article'}</T>
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '30px', 
          borderRadius: '8px', 
          marginBottom: '40px',
          boxShadow: 'var(--shadow)' 
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="article-title" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}><T>Title</T></label>
              <input 
                id="article-title"
                name="title"
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="article-content" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}><T>Content</T></label>
              <textarea 
                id="article-content"
                name="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '200px' }}
                required
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="article-images" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Article images (up to 3)
              </label>
              <input
                id="article-images"
                name="articleImages"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelection}
                style={{ width: '100%' }}
              />
              {selectedImages.length > 0 && (
                <div style={{ marginTop: '0.6rem', color: '#555', fontSize: '0.9rem' }}>
                  {selectedImages.length} image(s) selected.
                </div>
              )}
              {imageError && (
                <div style={{ marginTop: '0.5rem', color: '#b42318', fontSize: '0.9rem' }}>
                  {imageError}
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-secondary" disabled={publishing}>
              <T>{publishing ? 'Publishing...' : 'Publish Article'}</T>
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div><T>Loading articles...</T></div>
      ) : (
        <div style={{ display: 'grid', gap: '2rem' }}>
          {visibleArticles.length > 0 && (
            visibleArticles.map((article) => (
              (() => {
                const articleImages = (article.imageUrls && article.imageUrls.length > 0)
                  ? article.imageUrls.slice(0, MAX_ARTICLE_IMAGES)
                  : (article.imageUrl ? [article.imageUrl] : []);

                return (
              <article key={article.id} style={{ 
                padding: '30px', 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                boxShadow: 'var(--shadow)',
                border: '1px solid #eee'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h2 style={{ color: 'var(--primary-color)', marginBottom: '0.75rem' }}><T>{article.title}</T></h2>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      backgroundColor: (article.status || 'approved') === 'approved' ? '#e8f5e9' : '#fff3e0',
                      color: (article.status || 'approved') === 'approved' ? '#2e7d32' : '#ef6c00'
                    }}>
                      {(article.status || 'approved').toUpperCase()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {role === 'admin' && (article.status || 'approved') === 'pending' && (
                      <button
                        onClick={() => handleApprove(article.id)}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px' }}
                      >
                        <CheckCircle size={16} /> Approve
                      </button>
                    )}
                    {(role === 'admin' || (role === 'lawyer' && user?.uid === article.authorId)) && (
                      <button 
                        onClick={() => handleDelete(article.id)}
                        style={{ background: 'none', color: '#dc3545', border: 'none', cursor: 'pointer' }}
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <User size={16} /> <T>{article.authorName}</T>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Calendar size={16} /> 
                    {article.createdAt?.toDate ? article.createdAt.toDate().toLocaleDateString() : 'Just now'}
                  </span>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', color: '#444' }}>
                  <T>{article.content}</T>
                </div>
                {articleImages.length > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: articleImages.length > 1 ? 'repeat(auto-fit, minmax(180px, 1fr))' : 'minmax(220px, 420px)',
                      gap: '12px',
                      marginTop: '1rem',
                    }}
                  >
                    {articleImages.map((imageUrl, index) => (
                      <img
                        key={`${article.id}-image-${index}`}
                        src={imageUrl}
                        alt={`${article.title} image ${index + 1}`}
                        loading="lazy"
                        style={{ width: '100%', borderRadius: '10px', border: '1px solid #e5e7eb', objectFit: 'cover' }}
                      />
                    ))}
                  </div>
                )}
                {renderShareActions(article.id, article.title)}
              </article>
                );
              })()
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ArticlesPage;
